import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { KibChipChoice } from '@chewy/kib-chips-react'
import { KibAttachIcon } from '@chewy/kib-icons-react'
import classNames from 'classnames'

import DialogNames from '~/constants/DialogNames'
import DocumentDialogStates from '~/constants/DocumentDialogStates'
import FeatureToggle from '~/constants/featureToggle'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { ConversationFile } from '~/types'
import useDialog from '~/utils/useDialog'

import { useChatContainer } from '../context/ChatContainerContext'

import '@chewy/kib-chips-styles/dist/styles/kib-chip-choice.css'
import styles from './MessageAttachment.module.scss'

const MessageAttachment = ({
  attachment,
  choiceGroupName,
  hidden,
}: {
  attachment: ConversationFile
  choiceGroupName: string
  hidden: boolean
}) => {
  const { id, name } = attachment
  const { selectedAttachment, setSelectedAttachment } = useChatContainer()
  const selectedValue = selectedAttachment?.id
  const isSelected = selectedValue === id
  const isEnableCommunicationsRightRailEnabled = useSelector(
    getFeatureToggle(FeatureToggle.ENABLE_COMMUNICATIONS_RIGHT_RAIL),
  )
  const [openDocumentDialog, , isDocumentDialogOpen] = useDialog(
    DialogNames.DOCUMENT,
  )
  const prevDocumentDialogOpenRef = useRef(false)
  const onPreviewRequested = (file: ConversationFile) => {
    openDocumentDialog({
      previewOnly: true,
      document: file,
      PreviewProps: {
        hideTopButtons: true,
        view: true,
        showChangeFile: false,
      },
      step: DocumentDialogStates.PREVIEW,
      conversationId: file.conversationId,
    })
  }

  const handleSelection = () => {
    if (!isEnableCommunicationsRightRailEnabled) {
      onPreviewRequested(attachment)
    }

    setSelectedAttachment(attachment)
  }

  useEffect(() => {
    const wasOpen = prevDocumentDialogOpenRef.current
    prevDocumentDialogOpenRef.current = isDocumentDialogOpen

    if (wasOpen && !isDocumentDialogOpen) {
      setSelectedAttachment(null)
    }
  }, [isDocumentDialogOpen, setSelectedAttachment])

  return (
    <KibChipChoice
      className={classNames(styles.attachmentChip, {
        [styles.selected]: isSelected,
        [styles.unselected]: !isSelected,
        [styles.hidden]: hidden,
      })}
      icon={<KibAttachIcon />}
      id={id}
      name={choiceGroupName}
      selectedValue={selectedValue}
      value={id}
      onChange={handleSelection}
    >
      {name}
    </KibChipChoice>
  )
}

export default MessageAttachment
